import React, { useEffect, useState, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../lib/apiClient';

interface Payment {
  id: string;
  amount: number | string;
  status: string;
  created_at: string;
  user?: { id: number; name: string; email: string };
  plan?: { name: string };
}

interface Pagination {
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-700',
    pending: 'bg-slate-100 text-slate-600',
    refunded: 'bg-orange-50 text-orange-700',
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${map[status] ?? 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
}

export default function PaymentHistory() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const fetchPayments = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = { page };
    if (search) params.search = search;

    apiClient.get('/admin/payments', { params })
      .then(({ data }) => {
        setPayments(data.data ?? []);
        setPagination({
          total: data.total,
          current_page: data.current_page,
          per_page: data.per_page,
          last_page: data.last_page,
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const totalRevenue = payments.reduce((sum, p) => sum + (p.status === 'completed' ? Number(p.amount ?? 0) : 0), 0);

  return (
    <div className="px-4 sm:px-8 py-4 sm:py-8 max-w-[1200px] mx-auto flex flex-col gap-6 pb-20">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-extrabold text-slate-900 tracking-tight">Payment History</h1>
          <p className="text-[13px] text-slate-500 mt-1">
            {pagination ? `${pagination.total.toLocaleString()} transactions` : 'Loading...'}
          </p>
        </div>
        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search user..."
            value={search}
            onChange={handleSearch}
            className="pl-9 pr-3 py-2 bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-ink transition-colors w-60"
          />
        </div>
      </div>

      {/* Revenue summary from current page */}
      {payments.length > 0 && (
        <div className="bg-ink px-6 py-4 flex items-center gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.15em] text-white/40 mb-0.5">Page Revenue (completed)</p>
            <p className="text-[22px] font-extrabold text-white">${totalRevenue.toFixed(2)}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-left min-w-[560px]">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.12em] border-b border-slate-100">
              <th className="px-6 py-3.5">ID</th>
              <th className="px-6 py-3.5">User</th>
              <th className="px-6 py-3.5">Plan</th>
              <th className="px-6 py-3.5">Amount</th>
              <th className="px-6 py-3.5">Status</th>
              <th className="px-6 py-3.5">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <td key={j} className="px-6 py-4">
                      <div className="h-4 bg-slate-100 animate-pulse" style={{ width: `${40 + (j % 4) * 15}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : payments.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-400 text-sm">No payments found.</td>
              </tr>
            ) : (
              payments.map(p => (
                <tr key={p.id} className="hover:bg-slate-50/60 transition-colors border-l-2 border-transparent hover:border-slate-300">
                  <td className="px-6 py-3.5 text-[11px] font-mono text-slate-400">
                    {p.id.substring(0, 8)}…
                  </td>
                  <td className="px-6 py-3.5">
                    <p className="text-[13px] font-medium text-slate-700">{p.user?.name ?? '—'}</p>
                    <p className="text-[11px] text-slate-400">{p.user?.email ?? ''}</p>
                  </td>
                  <td className="px-6 py-3.5 text-[13px] text-slate-600">{p.plan?.name ?? 'N/A'}</td>
                  <td className="px-6 py-3.5">
                    <span className={`text-[14px] font-extrabold ${p.status === 'completed' ? 'text-emerald-600' : 'text-slate-500'}`}>
                      ${Number(p.amount ?? 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-3.5"><StatusBadge status={p.status} /></td>
                  <td className="px-6 py-3.5 text-[12px] text-slate-500">
                    {new Date(p.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        </div>
        {pagination && pagination.last_page > 1 && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
            <span className="text-[12px] text-slate-400 font-medium">
              Page <span className="font-bold text-slate-600">{pagination.current_page}</span> of{' '}
              <span className="font-bold text-slate-600">{pagination.last_page}</span>
              {' '}— <span className="font-bold text-slate-600">{pagination.total.toLocaleString()}</span> total
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pagination.current_page === 1}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.last_page, p + 1))}
                disabled={pagination.current_page === pagination.last_page}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
